import { useEffect, useRef } from "react";
import { X, PlayCircle } from "lucide-react";

/**
 * Modal video player for the in-app tutorial.
 *
 * Renders only when `open` is true. Auto-plays the video on open, pauses + rewinds
 * on close. Backdrop click and Esc both close the modal.
 */
export default function TutorialModal({ open, onClose }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!open) {
      try {
        videoRef.current?.pause();
        if (videoRef.current) videoRef.current.currentTime = 0;
      } catch {
        /* ignore */
      }
      return;
    }
    // small delay so the <video> has mounted
    const t = setTimeout(() => {
      videoRef.current?.play().catch(() => {});
    }, 50);
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-zinc-950/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="tutorial-modal"
    >
      <div
        className="w-full max-w-4xl bg-zinc-950 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <PlayCircle className="w-5 h-5 text-emerald-400" />
            <p className="font-heading text-white text-base">How to use Clinico Translate</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close tutorial"
            className="text-zinc-400 hover:text-white transition-colors"
            data-testid="tutorial-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="bg-black aspect-video">
          <video
            ref={videoRef}
            controls
            playsInline
            preload="metadata"
            poster="/tutorial-poster.jpg"
            className="w-full h-full"
            data-testid="tutorial-video"
          >
            <source src="/tutorial.mp4" type="video/mp4" />
            <source src="/tutorial.webm" type="video/webm" />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="px-5 py-3 text-xs text-zinc-500 border-t border-white/10 flex items-center justify-between">
          <span>A 60-second walkthrough of the translator, phrase library, and conversation mode.</span>
          <button
            onClick={onClose}
            className="text-zinc-300 hover:text-white"
            data-testid="tutorial-modal-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
